import { z } from "zod/v4";

// Branch Info Schema and Type
export const BranchInfoSchema = z.object({
	name: z.string(),
	upstream: z.string().nullable(),
	commitHash: z.string(),
	lastLog: z.string().nullable(),
	lastCommitTime: z.string().datetime().nullable(),
});

export type BranchInfo = z.infer<typeof BranchInfoSchema>;

// Git Repository Status
export const GitRepoStatusSchema = z.object({
	isClean: z.boolean(),
	isDraft: z.boolean(),
	isConflicted: z.boolean().optional(),
	ahead: z.number(),
	behind: z.number(),
	branch: z.string(),
	commit: z.string(),
	changed: z.number(),
	insertions: z.number(),
	deletions: z.number(),
	files: z.array(
		z.object({
			from: z.string().optional(),
			path: z.string(),
			index: z.string(),
			working_dir: z.string(),
		})
	),
});

export type GitRepoStatus = z.infer<typeof GitRepoStatusSchema>;

// Git Remote
export const GitRemoteSchema = z.object({
	name: z.string(),
	url: z.string(),
});

export type GitRemote = z.infer<typeof GitRemoteSchema>;

// Git Repository History
export const GitRepoHistorySchema = z.object({
	commits: z.array(
		z.object({
			message: z.string(),
			date: z.string(),
			hash: z.string(),
			author: z.string(),
			parents: z.array(z.string()),
			/** JJ change ID (revision ID). Only present for JJ-backed repos. */
			change_id: z.string().optional(),
			/** Whether this commit has unresolved conflicts (JJ-specific). */
			has_conflict: z.boolean().optional(),
			/** Whether this commit is immutable (ancestor of trunk/tags/untracked remote bookmarks). */
			immutable: z.boolean().optional(),
			/** Whether this commit is empty (no changes vs parent). */
			is_empty: z.boolean().optional(),
		})
	),
});

export type GitRepoHistory = z.infer<typeof GitRepoHistorySchema>;

// Git Show (for viewing commit details)
export const GitShowSchema = z.object({
	originalDiff: z.string(),
	header: z.object({
		prettyCommitSha: z.string(),
		authorName: z.string(),
		authorEmail: z.string(),
		prettyDate: z.string(),
		timeSinceCommit: z.string(),
		commitMessage: z.string(),
	}),
	contents: z.array(z.string()),
	parsedFiles: z.array(
		z.object({
			type: z.enum(["added", "removed", "renamed", "modified"]),
			aPath: z.string(),
			bPath: z.string(),
			hunks: z.array(
				z.object({
					aLineStart: z.number(),
					aNumLines: z.number(),
					bLineStart: z.number(),
					bNumLines: z.number().optional(),
					lines: z.array(
						z.object({
							type: z.enum(["insertion", "deletion", "context"]),
							content: z.string(),
						})
					),
				})
			),
		})
	),
});

export type GitShow = z.infer<typeof GitShowSchema>;

// Git Diff Types (imported from data/git/types)
export const GitDiffHunkSchema = z.object({
	aLineStart: z.number(),
	aNumLines: z.number(),
	bLineStart: z.number(),
	bNumLines: z.number().optional(),
	lines: z.array(
		z.object({ type: z.enum(["insertion", "deletion", "context"]), content: z.string() })
	),
});

export type GitDiffHunk = z.infer<typeof GitDiffHunkSchema>;

export const GitDiffFileSchema = z.object({
	type: z.enum(["added", "removed", "renamed", "modified", "Conflicted"]),
	aPath: z.string(),
	bPath: z.string(),
	hunks: z.array(GitDiffHunkSchema),
	/** Raw materialized content for conflicted files (with conflict markers). */
	conflictedContent: z.string().optional(),
});

export type GitDiffFile = z.infer<typeof GitDiffFileSchema>;

export const GitDiffSchema = z.object({
	parsedFiles: z.array(GitDiffFileSchema),
});

export type GitDiff = z.infer<typeof GitDiffSchema>;

// Git Ahead/Behind (commit lists from Rust)
export const GitAheadBehindCommitSchema = z.object({
	message: z.string(),
	date: z.string(),
	hash: z.string(),
	author: z.string(),
	parents: z.array(z.string()),
	change_id: z.string().optional(),
	has_conflict: z.boolean().optional(),
	immutable: z.boolean().optional(),
	is_empty: z.boolean().optional(),
});

export type GitAheadBehindCommit = z.infer<typeof GitAheadBehindCommitSchema>;

export const GitAheadBehindSchema = z.object({
	ahead: z.array(GitAheadBehindCommitSchema),
	behind: z.array(GitAheadBehindCommitSchema),
});
export type GitAheadBehind = z.infer<typeof GitAheadBehindSchema>;

// Input Types for Git Operations

export type GitCommitInput = {
	message: string;
};

export type GitDescribeInput = {
	message: string;
	revision?: string;
};

export type GitDiffRefsInput = {
	aSide: string;
	bSide: string;
};

export type GitFetchInput = {
	remote?: string;
	branch?: string;
};

export type GitPushInput = {
	remote?: string;
	branch?: string;
	sourceCommit?: string;
};

export type GitRemoteAddInput = {
	remote: string;
	url: string;
};

export type GitShowInput = {
	commitHash: string;
};

export type GitStashSaveInput = {
	message?: string;
};

export type GitStashPopInput = {
	index: number;
};

export type GitBranchNewInput = {
	name: string;
	startPoint?: string;
};

export type GitBranchSwitchInput = {
	target: string;
};

export type GitRebaseBookmarkInput = {
	localBranch: string;
	/** Any valid git ref — remote-tracking (refs/remotes/moment/main) or local (refs/heads/collab/main) */
	targetRef: string;
};

export type GitBranchAnalyzeInput = {
	source: string;
	target: string;
};

export type GitBranchDeleteInput = {
	name: string;
	force: boolean;
};

export type GitBranchTrackInput = {
	remote: string;
	localBranch: string;
	upstreamBranch: string;
};

export type GitMoveCommitInput = {
	commitHash: string;
	targetBranch: string;
};

export type GitBranchAheadBehindInput = {
	documentId: string;
	source: string;
	target: string;
};

export type GitRestoreToParentInput = {
	commitHash: string;
};

// Queue Command Type
export type QueuedCommand = {
	id: string;
	operation: string;
	input: any;
	resolve: (value: any) => void;
	reject: (error: any) => void;
};

export const GitMergeAnalysisSchema = z.object({
	none: z.boolean(),
	normal: z.boolean(),
	up_to_date: z.boolean(),
	fast_forward: z.boolean(),
	unborn: z.boolean(),
	conflicts: z
		.object({
			diff_ours: GitDiffFileSchema,
			diff_theirs: GitDiffFileSchema,
			conflicted_content: z.string(),
			ours_label: z.string().optional(),
			theirs_label: z.string().optional(),
		})
		.nullish(),
});

export type GitMergeAnalysis = z.infer<typeof GitMergeAnalysisSchema>;
